import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { EwLanguageService } from './module/e-warranty/services/ew-language.service';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NoPreloading, provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
// import { cacheInterceptor, errorInterceptor, usersInterceptor } from '@insite-store360/auth'; // cacheInterceptor moved to auth package
import { duplicateRequestInterceptor, errorInterceptor, noCacheInterceptor, usersInterceptor } from '@insite-store360/auth';
import { provideToastr } from 'ngx-toastr';
import { appRoutes } from './app.routes';

// Initialize Firebase only in browser environment
// if (typeof window !== 'undefined' && typeof document !== 'undefined') {
//   import('firebase/app').then(({ initializeApp }) => {
//     initializeApp(environment.firebaseConfig);
//   }).catch(error => {
//     console.error('Failed to initialize Firebase:', error);
//   });
// }

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

// Add this function to suppress DevTools errors
function suppressDevToolsErrors() {
  if (typeof window !== 'undefined') {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const message = args[0];
      if (typeof message === 'string' &&
        (message.includes('Minified React error #31') ||
          message.includes('inspector.b9415ea5.js') ||
          message.includes('[object Promise]'))) {
        // Suppress DevTools React errors
        return;
      }
      originalConsoleError.apply(console, args);
    };
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withPreloading(NoPreloading),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately'
    }),
    provideAnimationsAsync(),
    provideHttpClient(
      withFetch(),
      // withInterceptors([usersInterceptor, errorInterceptor, cacheInterceptor]) // cacheInterceptor moved to auth package
      // duplicateRequestInterceptor should be first to cancel duplicate requests early
      withInterceptors([duplicateRequestInterceptor, noCacheInterceptor, usersInterceptor, errorInterceptor])
    ),
    provideNativeDateAdapter(),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true,
      maxOpened: 3,
      autoDismiss: true,
      newestOnTop: true,
      enableHtml: false
    }),
    provideTranslateService({
      defaultLanguage: 'en',
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    // Apply the saved e-Warranty language at boot (English by default).
    // Returns the load observable so translations are ready before first paint.
    provideAppInitializer(() => inject(EwLanguageService).init()),
    {
      provide: 'ERROR_HANDLING',
      useFactory: () => {
        if (isDevMode()) {
          // In development, suppress DevTools errors
          const originalError = console.error;
          console.error = (...args) => {
            const message = args[0];
            if (typeof message === 'string' &&
              (message.includes('Minified React error #31') ||
                message.includes('inspector.b9415ea5.js'))) {
              return;
            }
            originalError.apply(console, args);
          };
        }
        return true;
      }
    }
  ],
};

// Call the error suppression function
suppressDevToolsErrors();
